import React, { useState } from "react";
import config from "../../utils/Config";
import {
  DEFAULT_MAP_POSITION,
  DEFAULT_ZOOM,
  elementWrapperID,
  getInitialLocation,
  IAddressData,
  ILocationData,
  inputID,
  mapID,
  SEARCH_MAP_ZOOM,
} from "./AddressMapHelper";
import { AddressWrapper } from "./AddressMap.style";
import { Map, useMap, useMapsLibrary } from "@vis.gl/react-google-maps";
import { US_PLACES_REGION_CODES } from "hhn-commons";
import AddressMapInput from "./AddressMapInput";
import AddressMapMarker from "./AddressMapMarker";

type IAddressMapProps = {
  onSelectAddress: (addressData: IAddressData) => void;
  initialAddressData: IAddressData;
  closeMapDialog: () => void;
  buttonLabel?: string;
};

const AddressMap = ({
  onSelectAddress,
  initialAddressData,
  closeMapDialog,
  buttonLabel,
}: IAddressMapProps) => {
  const map = useMap(mapID);
  const placesLibrary = useMapsLibrary("places");
  const geocodingLibrary = useMapsLibrary("geocoding");
  const [addressData, setAddressData] = useState<IAddressData>(
    getInitialLocation(initialAddressData),
  );
  const [mapCenter, setMapCenter] = useState<ILocationData>(
    initialAddressData.placeName
      ? { lat: initialAddressData.lat, lng: initialAddressData.lng }
      : DEFAULT_MAP_POSITION,
  );
  const [isAddressResolved, setIsAddressResolved] = useState<boolean>(
    Boolean(initialAddressData.placeName) &&
      initialAddressData.lat !== 0 &&
      initialAddressData.lng !== 0,
  );

  const onPlaceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setAddressData({
      ...addressData,
      placeName: value,
    });
    setIsAddressResolved(false);

    if (!geocodingLibrary || !placesLibrary) return;
    const geocoder = new (geocodingLibrary.Geocoder as any)();
    const inputEl = document.getElementById(inputID) as HTMLInputElement;
    const autocomplete = new (placesLibrary.Autocomplete as any)(inputEl, {
      componentRestrictions: { country: US_PLACES_REGION_CODES },
    });
    autocomplete.addListener("place_changed", async () => {
      const place = autocomplete.getPlace();
      if (!place.place_id) {
        return;
      }
      const { results } = await geocoder.geocode({ placeId: place.place_id });
      const position = results[0].geometry.location.toJSON();
      setMapCenter(position);
      const addressInfo: IAddressData = {
        lat: position.lat,
        lng: position.lng,
        placeName: results[0].formatted_address,
        apartmentNo: initialAddressData.apartmentNo || "",
      };
      setAddressData(addressInfo);
      setIsAddressResolved(true);
      if (map) {
        map.setZoom(SEARCH_MAP_ZOOM);
        map.setCenter(position);
      }
    });
  };

  const onMarkerDrag = async (data: {
    latLng: { toJSON: () => { lat: number; lng: number } };
  }) => {
    if (!geocodingLibrary) return;
    let position = data.latLng.toJSON();
    const geocoder = new (geocodingLibrary.Geocoder as any)();
    const { results } = await geocoder.geocode({ location: position });
    if (results[0]) {
      position = results[0].geometry.location.toJSON();
      const addressInfo: IAddressData = {
        lat: position.lat,
        lng: position.lng,
        placeName: results[0].formatted_address,
        apartmentNo: initialAddressData.apartmentNo || "",
      };
      setAddressData(addressInfo);
      setIsAddressResolved(true);
    }
  };

  const onSelectAddressClick = () => {
    if (!isAddressResolved) return;
    onSelectAddress(addressData);
    closeMapDialog();
  };

  const onClearAddress = () => {
    setAddressData({
      ...addressData,
      placeName: "",
    });
    setIsAddressResolved(false);
  };

  return (
    <Map
      mapId={config.googleMapId}
      id={mapID}
      style={{
        height: "70dvh",
      }}
      defaultCenter={mapCenter}
      defaultZoom={DEFAULT_ZOOM}
      gestureHandling="greedy"
      disableDefaultUI={true}
      fullscreenControl={false}
      mapTypeControl={false}
      streetViewControl={false}
    >
      <AddressWrapper id={elementWrapperID}>
        <AddressMapInput
          addressData={addressData}
          onChange={onPlaceChange}
          onClick={onSelectAddressClick}
          buttonLabel={buttonLabel}
          onClear={onClearAddress}
          disabled={!isAddressResolved}
        />
        <AddressMapMarker addressData={addressData} onDragEnd={onMarkerDrag} />
      </AddressWrapper>
    </Map>
  );
};

export default AddressMap;
